/**
 * StepperController
 * @class
 * @description Controls the interactive logic of a stepper component including navigation and validation.
 */
export default class StepperController {
  /**
   * @param {HTMLElement} element - The root element of the stepper
   * @param {object} options - Configuration options
   */
  constructor(element, options = {}) {
    if (!element) return;

    this.stepperElement = element;
    this.options = {
      activeClass: 'is-active',
      completedClass: 'is-completed',
      errorClass: 'has-error',
      ...options,
    };

    this.steps = Array.from(this.stepperElement.querySelectorAll('[data-step-target]'));
    this.contents = Array.from(this.stepperElement.querySelectorAll('[data-step-content]'));
    this.currentStepIndex = 0;

    this._init();
  }

  /**
   * Initialize the stepper
   * @private
   */
  _init() {
    this._bindEvents();
    this._updateUI();
  }

  /**
   * Bind event listeners
   * @private
   */
  _bindEvents() {
    this.stepperElement.addEventListener('click', event => {
      const { target } = event;

      if (target.closest('[data-step-next]')) {
        this.next();
      } else if (target.closest('[data-step-prev]')) {
        this.previous();
      } else if (target.closest('[data-step-target]')) {
        // Optional: allow direct navigation if already completed
        const stepIndex = this.steps.indexOf(target.closest('[data-step-target]'));
        if (this._canJumpTo(stepIndex)) {
          this.goTo(stepIndex);
        }
      }
    });
  }

  /**
   * Navigate to the next step
   * @public
   */
  next() {
    if (this.currentStepIndex < this.steps.length - 1) {
      if (this.validateCurrentStep()) {
        const previousStepIndex = this.currentStepIndex;
        this.currentStepIndex += 1;
        this._updateUI();
        this._dispatch('stepper:next', {
          currentStep: this.currentStepIndex,
          previousStep: previousStepIndex,
        });
        this._dispatch('stepper:change', {
          currentStep: this.currentStepIndex,
          previousStep: previousStepIndex,
        });
      }
    } else if (this.currentStepIndex === this.steps.length - 1) {
      if (this.validateCurrentStep()) {
        this._dispatch('stepper:complete', { currentStep: this.currentStepIndex });
      }
    }
  }

  /**
   * Navigate to the previous step
   * @public
   */
  previous() {
    if (this.currentStepIndex > 0) {
      const previousStepIndex = this.currentStepIndex;
      this.currentStepIndex -= 1;
      this._updateUI();
      this._dispatch('stepper:previous', {
        currentStep: this.currentStepIndex,
        previousStep: previousStepIndex,
      });
      this._dispatch('stepper:change', {
        currentStep: this.currentStepIndex,
        previousStep: previousStepIndex,
      });
    }
  }

  /**
   * Jump to a specific step
   * @param {number} index
   * @public
   */
  goTo(index) {
    if (index >= 0 && index < this.steps.length && index !== this.currentStepIndex) {
      const previousStepIndex = this.currentStepIndex;
      this.currentStepIndex = index;
      this._updateUI();
      this._dispatch('stepper:change', {
        currentStep: this.currentStepIndex,
        previousStep: previousStepIndex,
      });
    }
  }

  /**
   * Validates all fields in the current step content
   * @returns {boolean}
   * @public
   */
  validateCurrentStep() {
    const currentContent = this.contents[this.currentStepIndex];
    if (!currentContent) return true;

    const inputs = Array.from(currentContent.querySelectorAll('input, select, textarea'));
    const invalidFields = inputs.filter(input => {
      // Ignora campos dentro de elementos com atributo [hidden] (HTML nativo)
      if (input.closest('[hidden]')) return false;

      // Ignora campos ocultos via CSS (display: none em qualquer ancestral)
      if (!input.offsetParent && input.type !== 'hidden') return false;

      // Basic HTML5 validation
      let isValid = input.checkValidity();

      // Custom data-validator check
      if (isValid && input.dataset.validator) {
        isValid = this._runCustomValidator(input);
      }

      return !isValid;
    });

    const isValid = invalidFields.length === 0;
    this._setStepError(this.currentStepIndex, invalidFields.length);

    if (!isValid) {
      this._dispatch('stepper:error', {
        currentStep: this.currentStepIndex,
        errorsCount: invalidFields.length,
        invalidFields,
      });
    }

    return isValid;
  }

  /**
   * Run custom validation logic based on data-validator attribute
   * @param {HTMLElement} input
   * @returns {boolean}
   * @private
   */
  _runCustomValidator(input) {
    // This could be extended via options or a global registry
    const validatorName = input.dataset.validator;
    if (this.options.validators && this.options.validators[validatorName]) {
      return this.options.validators[validatorName](input);
    }
    return true;
  }

  /**
   * Updates the visual state of steps and contents
   * @private
   */
  _updateUI() {
    this.steps.forEach((step, index) => {
      // Steps classes
      step.classList.toggle(this.options.activeClass, index === this.currentStepIndex);
      step.classList.toggle(this.options.completedClass, index < this.currentStepIndex);

      // Accessibility
      step.setAttribute('aria-current', index === this.currentStepIndex ? 'step' : 'false');
      const stepButton = step.querySelector('button, a');
      if (stepButton) {
        stepButton.setAttribute('aria-disabled', index > this.currentStepIndex);
      }
    });

    this.contents.forEach((content, index) => {
      content.style.display = index === this.currentStepIndex ? 'block' : 'none';
      content.classList.toggle(this.options.activeClass, index === this.currentStepIndex);
    });
  }

  /**
   * Sets or removes error state and badge for a step
   * @param {number} index
   * @param {number} errorCount
   * @private
   */
  _setStepError(index, errorCount) {
    const step = this.steps[index];
    if (!step) return;

    step.classList.toggle(this.options.errorClass, errorCount > 0);
    this._renderErrorBadge(step, errorCount);
  }

  /**
   * Renders error badge with count
   * @param {HTMLElement} stepElement
   * @param {number} count
   * @private
   */
  _renderErrorBadge(stepElement, count) {
    let badge = stepElement.querySelector('.step-error-badge');

    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'step-error-badge badge rounded-pill';
        const iconContainer = stepElement.querySelector('.step-icon');
        if (iconContainer) {
          iconContainer.appendChild(badge);
        } else {
          stepElement.appendChild(badge);
        }
      }
      badge.textContent = count;
    } else if (badge) {
      badge.remove();
    }
  }

  /**
   * Check if user can jump to a specific step
   * @param {number} index
   * @returns {boolean}
   * @private
   */
  _canJumpTo(index) {
    // Only allow jumping back or to the very next step
    return index < this.currentStepIndex;
  }

  /**
   * Dispatch custom events
   * @param {string} name
   * @param {object} detail
   * @private
   */
  _dispatch(name, detail) {
    const event = new CustomEvent(name, {
      bubbles: true,
      detail,
    });
    this.stepperElement.dispatchEvent(event);
  }
}
